import { Component, ElementRef, Input, OnInit, ViewChild, inject, Renderer2, viewChild, AfterViewInit } from '@angular/core';
import { ButtonCloseDirective, OffcanvasService, ThemeDirective } from '@coreui/angular';
import { OffcanvasLevelCheckPipe } from './offcanvas-level-check.pipe';
import { OffCanvasPlacement, OptimaOffCanvasComponent, OptimaOffCanvasService } from '@optima-stride-ai/optima-components';
import { FormSelectorPipe } from './form-selector.pipe';
import { FormStates, OffCanvasComponents, OffCanvasRightContentTypes } from '../off-canvas-service/off-canvas-form.service';
import { FormAddDashboardComponent } from '../../pages/dashboard/dashboard-component/form-add-dashboard/form-add-dashboard.component';
import { AppStateService } from '../../services/state-store/app-state';
import { FormAddLibraryComponent } from "../../pages/dashboard/dashboard-component/form-add-library/form-add-library.component";
import { FormUpdateLibraryComponent } from '../../pages/dashboard/dashboard-component/form-update-library/form-add-library.component';
import { FormAddFolderComponent } from '../../pages/dashboard/dashboard-component/form-add-folder/form-add-folder.component';
import { FormAddFileComponent } from '../../pages/dashboard/dashboard-component/form-add-file/form-add-file.component';
import { FormSaveAsTemplateComponent } from '../../pages/dashboard/dashboard-component/form-save-as-template/form-save-as-template.component';
import { FormBizEntityComponent } from '@optima-stride-ai/optima-app-framework';
import { FormBizEntityTypeComponent } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeCategory, IBizEntity, IBizEntityType, LoggerService } from '@optima-stride-ai/core';
import { Subscription } from 'rxjs';
import { GlobalStateService } from '../../../service/global.service';


@Component({
  selector: 'app-off-canvas-form',
  imports: [
    OffcanvasLevelCheckPipe,
    ButtonCloseDirective,
    ThemeDirective,
    FormSelectorPipe,
    OptimaOffCanvasComponent,
    FormAddDashboardComponent,
    FormAddLibraryComponent,
    FormAddLibraryComponent,
    FormUpdateLibraryComponent,
    FormAddFolderComponent,
    FormAddFolderComponent,
    FormAddFileComponent,
    FormSaveAsTemplateComponent,
    FormBizEntityComponent,
    FormBizEntityTypeComponent,
  ],
  templateUrl: './off-canvas-form.component.html',
  styleUrl: './off-canvas-form.component.sass'
})
export class OffCanvasComponentSelector implements OnInit, AfterViewInit {
  @Input() id: string = "";
  @Input() placement: OffCanvasPlacement = OffCanvasPlacement.End;
  @Input() level: number = 1;

  bizEntity!: IBizEntity;
  bizEntityTypeForEdit!: IBizEntityType;
  isEditing: boolean = false;

  private offCanvasServiceRef = inject(OffcanvasService);
  LibFormBizEntityTypeComponent = FormBizEntityTypeComponent;
  offCanvasFormService = inject(OptimaOffCanvasService)
  private appStateService: AppStateService = inject(AppStateService);
  private readonly globalState = inject(GlobalStateService);
  private readonly logger = inject(LoggerService);
  private offCanvasStateSubscription?: Subscription;
  offCanvasComponents = OffCanvasComponents;
  LibFormBizEntityComponent = FormBizEntityComponent; // Need to assign to a variable to use in template
  get childComp() { return OffCanvasComponents }

  offCanvasClass() {
    if (this.level == 1) return 'optima-off-canvas'
    else return `optima-off-canvas-l-${this.level} optima-off-leveled`;
  }

  renderErrors = false;
  constructor(private renderer: Renderer2) {
    this.offCanvasServiceRef.offcanvasState$.subscribe((state) => {
      if (state.id !== this.id) return; // We have multiple levels and if we don't put this condition, other levels will also run show/hide methods.
      if (state.show) {
        this.renderErrors = true; //if required render errors as form is visible and showing error makes sense
        this.patchFormValues()
      } else {
        this.renderErrors = false;  //don't render errors as form is not visible anyways. (if we show errors here, in some cases, errors will be shown for a fraction of second before form is closed)
        if (this.offCanvasFormService.getLastOffCanvasAtLevel(this.level)?.componentIsABizEntity) {
          // offcanvas is being closed and the child component is a biz entity component, let's clear the state in app state service.
          this.appStateService.entityFormState = FormStates.Idle;
          const comp = this.offCanvasFormService.getLastOffCanvasAtLevel(this.level)?.componentInOffCanvas
        } else if (this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.Others) {
          this.appStateService.offCanvasRightContentType = OffCanvasRightContentTypes.None;
          this.appStateService.offCanvasRightComponent = OffCanvasComponents.None;
        }
      }
    })
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.offCanvasStateSubscription = this.offCanvasServiceRef.offcanvasState$.subscribe((state) => {
      if (state.id !== this.id) return;

      if (state.show) {
        // Off-canvas is opening, get the entity data
        const data = this.offCanvasFormService.dataOfCurrentFormInView;

        if (data) {
          // Data can be either the entity directly (legacy) or an object with bizEntity and isEditing
          if (data.bizEntity) {
            this.bizEntity = data.bizEntity;
            this.isEditing = data.isEditing || false;
          } else {
            // Legacy: data is the entity directly
            this.bizEntity = data;
            this.isEditing = false;
          }
          if (data.bizEntityType) {
            this.bizEntityTypeForEdit = data.bizEntityType;
            this.isEditing = true;
          }
        }
      }
    });
  }


  private patchFormValues() {

  }

  onCloseButtonClicked() {
    this.offCanvasFormService.currentOffCanvas?.onClose!();
  }

  /**
   * Check if the current off-canvas should render a specific component
   */
  shouldRenderComponent(componentType: any): boolean {
    const currentOffCanvas = this.offCanvasFormService.getLastOffCanvasAtLevel(this.level);
    return currentOffCanvas?.componentInOffCanvas === componentType;
  }

  shouldExcludeParentAndOwner(): boolean {
    // return this.bizEntity?.type?.category !== BizEntityTypeCategory.Tag;
    return false;
  }

  /**
   * Bridge `saveComplete` events from the inline generic biz-entity form to
   * the same global signals used by the dedicated wrapper forms
   * (`FormAddFolderComponent`, `FormUpdateLibraryComponent`, etc.) so that
   * downstream listeners (e.g. the libraries page) refresh their data with
   * a shimmer/loading state — matching the behaviour of create / rename /
   * delete actions.
   */
  onBizEntitySaveComplete(event: { bizEntity: IBizEntity; operation: 'create' | 'update'; success: boolean }): void {
    if (!event?.success) {
      this.logger.warn(`Off-canvas biz-entity ${event?.operation ?? 'save'} did not succeed; skipping refresh signal.`);
      return;
    }

    if (event.operation === 'update') {
      this.globalState.setBizEntityUpdated(true, event.bizEntity);
    } else {
      this.globalState.setBizEntityCreated(true);
    }
  }

}